import _en from './translations/en.json.mjs';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useI18n } from '@shopify/react-i18n';
import { TextField } from '@shopify/polaris';
import { classNames } from '@shopify/css-utilities';
import styles from './TextPicker.scss.mjs';
import { isHexString, normalizeValue, expandHex, isHashlessHex, isRgbString, rgbStringToHex, isColorName, nameToHex } from '../../../../utilities/color/color.mjs';

const UPDATE_TIMEOUT = 500;
function TextPicker({
  color,
  onChange,
  customTextField,
  allowEmpty,
  disabled
}) {
  const [internalValue, setInternalValue] = useState(null);
  const [inputError, setInputError] = useState(false);
  const [i18n] = useI18n({
    id: 'TextPicker_7yp40z',
    fallback: _en,
    translations(locale) {
      if (["cs", "da", "de", "es", "fi", "fr", "it", "ja", "ko", "nb", "nl", "pl", "pt-BR", "pt-PT", "sv", "th", "tr", "vi", "zh-CN", "zh-TW"].indexOf(locale) < 0) {
        return;
      }
      return import( /* webpackChunkName: "TextPicker_7yp40z-i18n", webpackMode: "lazy-once" */`./translations/${locale}.json`).then(dict => dict && dict.default);
    }
  });
  const ignoreChangeRef = useRef(false);
  const error = inputError ? i18n.translate('invalidColor') : undefined;
  const value = internalValue != null ? internalValue : color;
  const valueForDisplay = isHexString(value) ? value.toUpperCase() : value;
  const handleUpdate = useCallback(() => {
    const validUserInput = coerceToValidUserInput(value);
    const validValue = Boolean(validUserInput) || allowEmpty && value === '';
    if (!validValue) {
      setInputError(true);
      return;
    }
    setInternalValue(null);
    setInputError(false);
    const colorHasChanged = validUserInput !== color;
    if (colorHasChanged) {
      ignoreChangeRef.current = true;
      onChange(validUserInput || '');
    }
  }, [color, onChange, value, allowEmpty]);
  useEffect(() => {
    if (!ignoreChangeRef.current) {
      setInternalValue(color);
    }
    ignoreChangeRef.current = false;
  }, [color]);
  const debouncedHandleUpdate = useDebouncedCallback(handleUpdate, UPDATE_TIMEOUT);
  useEffect(() => {
    if (internalValue !== null) {
      debouncedHandleUpdate();
    }
  }, [internalValue, handleUpdate, debouncedHandleUpdate]);
  if (customTextField) {
    return customTextField({
      onChange: setInternalValue,
      value: valueForDisplay,
      error
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    className: styles.TextFieldContainer
  }, /*#__PURE__*/React.createElement(TextField, {
    label: i18n.translate('label'),
    labelHidden: true,
    placeholder: color,
    value: valueForDisplay,
    onChange: setInternalValue,
    error: error,
    disabled: disabled,
    prefix: renderSelectedColor(),
    autoComplete: "off"
  }));
  function renderSelectedColor() {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.TextFieldSwatch
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.SwatchBackground),
      style: {
        backgroundColor: color
      }
    }));
  }
}
function coerceToValidUserInput(value) {
  const normalizedValue = normalizeValue(value);
  if (isHexString(normalizedValue)) return expandHex(normalizedValue);
  if (isHashlessHex(normalizedValue)) return expandHex(`#${normalizedValue}`);
  if (isRgbString(normalizedValue)) return rgbStringToHex(normalizedValue);
  if (isColorName(normalizedValue)) return nameToHex(normalizedValue);
  return null;
}

export { TextPicker, UPDATE_TIMEOUT };
